// src/views/parent/PANouvelleJustification.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useParentService } from "../../services/parentService";
import { useParentDrawer } from "../../context/ParentDrawerContext";
import Swal from "sweetalert2";

// ─── HELPER : extraction d'un message d'erreur DRF exploitable ───────────
function extractErrorMessage(err) {
  const data = err.response?.data;
  if (!data) return "Impossible de soumettre la justification. Vérifiez votre connexion.";

  if (typeof data === "string") return data;
  if (data.error) return data.error;
  if (data.detail) return data.detail;
  if (data.non_field_errors) return data.non_field_errors.join(" ");

  const messagesDeChamps = Object.values(data)
    .flat()
    .filter((v) => typeof v === "string");
  if (messagesDeChamps.length > 0) return messagesDeChamps.join(" ");

  return "Impossible de soumettre la justification.";
}

export default function PANouvelleJustification({ slug }) {
  const navigate = useNavigate();
  const { enfantId } = useParams();
  const { getEnfants, creerJustification } = useParentService();
  const { openDrawer } = useParentDrawer();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);     // chargement initial
  const [submitting, setSubmitting] = useState(false); // soumission en cours
  const [error, setError] = useState(null);
  const [enfant, setEnfant] = useState(null);
  const [inscriptionId, setInscriptionId] = useState(null);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedPeriode, setSelectedPeriode] = useState("journee");
  const [selectedMotif, setSelectedMotif] = useState("autre");
  const [description, setDescription] = useState("");
  const [pieceJointe, setPieceJointe] = useState(null);
  const [pieceJointeNom, setPieceJointeNom] = useState(null);

  const hasFetchedRef = useRef(false);

  const periodes = [
    { value: "matin", label: "Matin", icon: "ti ti-sun" },
    { value: "apres_midi", label: "Après-midi", icon: "ti ti-cloud" },
    { value: "journee", label: "Journée", icon: "ti ti-calendar-day" },
  ];

  const motifs = [
    { value: "maladie", label: "Maladie", icon: "🤒" },
    { value: "famille", label: "Événement familial", icon: "👨‍👩‍👧" },
    { value: "transport", label: "Problème de transport", icon: "🚌" },
    { value: "autre", label: "Autre", icon: "📝" },
  ];

  // ─── CHARGEMENT DE L'ENFANT ──────────────────────────────────────
  const loadEnfant = useCallback(async () => {
    if (!slug || !enfantId) {
      setError("Paramètres manquants (parent ou élève).");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const enfantsData = await getEnfants(slug);
      const enfantTrouve = (enfantsData || []).find((e) => e.id === parseInt(enfantId));

      if (!enfantTrouve) {
        setError("Élève introuvable.");
        setLoading(false);
        return;
      }

      setEnfant(enfantTrouve);
      const inscription = enfantTrouve?.inscriptions?.[0];
      setInscriptionId(inscription?.id || null);
    } catch (err) {
      setError("Impossible de charger les informations de l'élève.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, enfantId]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    loadEnfant();
  }, [loadEnfant]);

  // Reset du flag si enfantId change
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [enfantId]);

  // ─── HELPERS ─────────────────────────────────────────────────────────
  const formatDateForDisplay = (date) => {
    if (!date) return "";
    return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()}`;
  };

  const formatDateForApi = (date) => {
    if (!date) return "";
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
  };

  const pickFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPieceJointe(file);
      setPieceJointeNom(file.name);
    }
  };

  const showDatePicker = () => {
    const input = document.createElement("input");
    input.type = "date";
    input.max = formatDateForApi(new Date());
    input.value = formatDateForApi(selectedDate);
    input.onchange = (e) => {
      const [year, month, day] = e.target.value.split("-");
      setSelectedDate(new Date(year, month - 1, day));
    };
    input.click();
  };

  // ─── SOUMISSION ──────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedDate) {
      Swal.fire({
        icon: "warning",
        title: "Veuillez sélectionner une date",
        confirmButtonColor: "var(--pr)",
      });
      return;
    }

    if (!inscriptionId) {
      Swal.fire({
        icon: "error",
        title: "Inscription introuvable pour cet élève",
        confirmButtonColor: "var(--pr)",
      });
      return;
    }

    setSubmitting(true);

    try {
      const data = {
        inscription_id: parseInt(inscriptionId),
        date_absence: formatDateForApi(selectedDate),
        periode: selectedPeriode,
        motif: selectedMotif,
        description: description,
      };

      await creerJustification(slug, data, pieceJointe);

      await Swal.fire({
        icon: "success",
        title: "Justification soumise avec succès ✅",
        toast: true,
        timer: 2000,
        showConfirmButton: false,
        position: "top-right",
      });

      navigate(`/${slug}/parent/justifications/${enfantId}`);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Erreur lors de la soumission",
        text: extractErrorMessage(err),
        confirmButtonColor: "var(--pr)",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ─── RENDU ───────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');

        *, *::before, *::after { box-sizing: border-box; }

        .new-justif-page {
          min-height: 100vh;
          background: var(--s2);
        }

        .new-justif-header {
          background: linear-gradient(135deg, var(--pr-dk), var(--pr));
          padding: 36px 16px 28px;
          text-align: center;
          border-radius: 0 0 40px 40px;
          position: relative;
          overflow: hidden;
        }

        .new-justif-header::after {
          content: "";
          position: absolute;
          left: 20%;
          right: 20%;
          bottom: 0;
          height: 3px;
          background: linear-gradient(90deg, transparent, var(--or), transparent);
        }

        .new-justif-header .back-btn {
          position: absolute;
          top: 16px;
          left: 16px;
          background: rgba(255,255,255,0.2);
          border: none;
          border-radius: 12px;
          padding: 8px 10px;
          cursor: pointer;
          color: #fff;
          font-size: 20px;
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .new-justif-header .back-btn:hover {
          background: rgba(255,255,255,0.3);
        }

        .new-justif-header .menu-btn {
          position: absolute;
          top: 16px;
          right: 16px;
          background: rgba(255,255,255,0.2);
          border: none;
          border-radius: 12px;
          padding: 8px 10px;
          cursor: pointer;
          color: #fff;
          font-size: 20px;
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .new-justif-header .menu-btn:hover {
          background: rgba(255,255,255,0.3);
        }

        .new-justif-header .icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: rgba(255,255,255,0.2);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 8px;
        }

        .new-justif-header .icon-wrapper i {
          font-size: 24px;
          color: #fff;
        }

        .new-justif-header h1 {
          font-family: 'Poppins', sans-serif;
          color: #fff;
          font-size: 20px;
          font-weight: 700;
          margin: 0;
        }

        .new-justif-header p {
          font-family: 'Inter', sans-serif;
          color: rgba(255,255,255,0.75);
          font-size: 13px;
          margin: 4px 0 0;
        }

        /* ─── Loader intégré ─── */
        .new-justif-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          gap: 14px;
        }

        .new-justif-loading .spinner {
          width: 44px;
          height: 44px;
          border: 4px solid var(--pr-bg, #e5eef7);
          border-top-color: var(--pr);
          border-radius: 50%;
          animation: new-justif-spin 0.8s linear infinite;
        }

        @keyframes new-justif-spin { to { transform: rotate(360deg); } }

        .new-justif-loading span {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
        }

        /* ─── Carte d'erreur intégrée ─── */
        .new-justif-error {
          background: #fff;
          border-radius: 20px;
          padding: 40px 20px;
          text-align: center;
          box-shadow: 0 4px 18px rgba(0,0,0,0.08);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          margin: 16px;
        }

        .new-justif-error i.alert-icon {
          font-size: 48px;
          color: var(--rd);
        }

        .new-justif-error h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 17px;
          font-weight: 600;
          color: var(--t);
          margin: 4px 0 0;
        }

        .new-justif-error p {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
          margin: 0 0 12px;
        }

        .new-justif-error .actions-row {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .new-justif-error .btn {
          border: none;
          border-radius: 30px;
          padding: 10px 22px;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #fff;
          background: var(--pr);
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: opacity 0.2s;
        }

        .new-justif-error .btn:hover { opacity: 0.9; }

        .new-justif-error .btn.btn-secondary {
          background: var(--b2);
          color: var(--t);
        }

        /* ─── Enfant ─── */
        .new-justif-eleve {
          margin: 16px;
          padding: 14px 16px;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .new-justif-eleve .avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: var(--pr-bg);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          font-weight: 700;
          color: var(--pr);
          flex-shrink: 0;
        }

        .new-justif-eleve .info {
          flex: 1;
        }

        .new-justif-eleve .info .name {
          font-family: 'Poppins', sans-serif;
          font-size: 15px;
          font-weight: 700;
          color: var(--t);
          margin: 0 0 2px;
        }

        .new-justif-eleve .info .classe {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
          margin: 0;
        }

        .new-justif-eleve .warning {
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          color: var(--rd);
          margin-top: 4px;
        }

        /* ─── Formulaire ─── */
        .new-justif-form {
          padding: 0 16px 16px;
        }

        .new-justif-section {
          margin-bottom: 14px;
          padding: 14px 16px;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.04);
        }

        .new-justif-section .section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 14px;
          color: var(--t);
          margin-bottom: 10px;
        }

        .new-justif-section .section-title i {
          color: var(--pr);
        }

        .date-picker {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 14px;
          background: var(--pr-bg);
          border-radius: 10px;
          border: 1px solid rgba(27,79,128,0.25);
          cursor: pointer;
        }

        .date-picker i {
          color: var(--pr);
          font-size: 18px;
        }

        .date-picker span {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          color: var(--t);
          flex: 1;
        }

        .periode-options {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .periode-options .periode-btn {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 8px 14px;
          border-radius: 30px;
          border: 1px solid rgba(27,79,128,0.25);
          background: var(--pr-bg);
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--pr);
          transition: all 0.2s;
        }

        .periode-options .periode-btn.active {
          background: var(--pr);
          color: #fff;
        }

        .periode-options .periode-btn i {
          font-size: 14px;
        }

        .motif-select {
          padding: 8px 12px;
          background: var(--pr-bg);
          border-radius: 10px;
          border: 1px solid rgba(27,79,128,0.25);
        }

        .motif-select select {
          width: 100%;
          background: transparent;
          border: none;
          outline: none;
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          padding: 8px 4px;
          cursor: pointer;
          color: var(--t);
        }

        .description-textarea {
          width: 100%;
          padding: 12px 14px;
          background: var(--pr-bg);
          border-radius: 10px;
          border: 1px solid rgba(27,79,128,0.25);
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          resize: vertical;
          outline: none;
          min-height: 80px;
          color: var(--t);
        }

        .description-textarea::placeholder {
          color: var(--h);
        }

        .file-picker {
          padding: 16px 20px;
          background: var(--pr-bg);
          border-radius: 10px;
          border: 1px solid rgba(27,79,128,0.25);
          cursor: pointer;
          text-align: center;
          transition: all 0.2s;
        }

        .file-picker:hover {
          border-color: var(--pr);
        }

        .file-picker i {
          font-size: 32px;
          color: var(--pr);
        }

        .file-picker p {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
          margin: 8px 0 0;
        }

        .file-picker .sub {
          font-size: 11px;
          color: var(--h);
          margin: 4px 0 0;
        }

        .file-picker .file-selected {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .file-picker .file-selected i {
          font-size: 24px;
          color: var(--gn);
        }

        .file-picker .file-selected .name {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: var(--gn);
        }

        .file-picker .file-selected .remove-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: var(--rd);
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          margin-top: 4px;
        }

        .submit-btn {
          width: 100%;
          padding: 14px;
          background: var(--pr);
          border: none;
          border-radius: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-family: 'Inter', sans-serif;
          font-size: 15px;
          font-weight: 700;
          color: #fff;
          transition: background 0.2s;
          margin-top: 8px;
        }

        .submit-btn:hover:not(:disabled) {
          background: var(--pr-lt);
        }

        .submit-btn:disabled {
          background: var(--h);
          cursor: default;
        }

        /* Mini spinner pour le bouton submit */
        .submit-btn .btn-spinner {
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          border-radius: 50%;
          animation: new-justif-spin 0.7s linear infinite;
        }

        @media (max-width: 480px) {
          .new-justif-header {
            padding: 28px 16px 20px;
          }
          .new-justif-header h1 {
            font-size: 17px;
          }
        }
      `}</style>

      <div className="new-justif-page">
        {/* Header — TOUJOURS affiché */}
        <div className="new-justif-header">
          <button className="back-btn" onClick={() => navigate(-1)} aria-label="Retour">
            <i className="ti ti-arrow-left" />
          </button>

          <button className="menu-btn" onClick={openDrawer} aria-label="Ouvrir le menu">
            <i className="ti ti-menu-2" />
          </button>

          <div className="icon-wrapper">
            <i className="ti ti-file-text" />
          </div>
          <h1>Nouvelle justification</h1>
          <p>Remplissez le formulaire ci-dessous</p>
        </div>

        {loading ? (
          /* ── ÉTAT CHARGEMENT ── */
          <div className="new-justif-loading">
            <div className="spinner"></div>
            <span>Chargement…</span>
          </div>
        ) : error ? (
          /* ── ÉTAT ERREUR ── */
          <div className="new-justif-error">
            <i className="ti ti-alert-circle alert-icon" />
            <h2>Erreur de chargement</h2>
            <p>{error}</p>
            <div className="actions-row">
              <button
                className="btn"
                onClick={() => {
                  hasFetchedRef.current = false;
                  loadEnfant();
                }}
              >
                <i className="ti ti-refresh" />
                Réessayer
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate(`/${slug}/parent/accueil`)}
              >
                <i className="ti ti-home" />
                Accueil
              </button>
            </div>
          </div>
        ) : (
          /* ── CONTENU (formulaire) ── */
          <>
            {/* Enfant */}
            {enfant && (
              <div className="new-justif-eleve">
                <div className="avatar">
                  {enfant.prenom?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="info">
                  <p className="name">{enfant.prenom} {enfant.nom}</p>
                  <p className="classe">
                    <i className="ti ti-school" style={{ marginRight: 4 }} />
                    Classe : {enfant.inscriptions?.[0]?.classe_nom || "-"}
                  </p>
                  {!inscriptionId && (
                    <p className="warning">
                      <i className="ti ti-alert-circle" style={{ marginRight: 4 }} />
                      Aucune inscription active
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Formulaire */}
            <div className="new-justif-form">
              {/* Date */}
              <div className="new-justif-section">
                <div className="section-title">
                  <i className="ti ti-calendar" />
                  Date d'absence
                </div>
                <div className="date-picker" onClick={showDatePicker}>
                  <i className="ti ti-calendar" />
                  <span>{selectedDate ? formatDateForDisplay(selectedDate) : "Sélectionner une date"}</span>
                </div>
              </div>

              {/* Période */}
              <div className="new-justif-section">
                <div className="section-title">
                  <i className="ti ti-clock" />
                  Période
                </div>
                <div className="periode-options">
                  {periodes.map((periode) => (
                    <button
                      key={periode.value}
                      className={`periode-btn ${selectedPeriode === periode.value ? "active" : ""}`}
                      onClick={() => setSelectedPeriode(periode.value)}
                    >
                      <i className={periode.icon} />
                      {periode.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Motif */}
              <div className="new-justif-section">
                <div className="section-title">
                  <i className="ti ti-list" />
                  Motif
                </div>
                <div className="motif-select">
                  <select
                    value={selectedMotif}
                    onChange={(e) => setSelectedMotif(e.target.value)}
                  >
                    {motifs.map((motif) => (
                      <option key={motif.value} value={motif.value}>
                        {motif.icon} {motif.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div className="new-justif-section">
                <div className="section-title">
                  <i className="ti ti-file-text" />
                  Description (optionnelle)
                </div>
                <textarea
                  className="description-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Ex : Fièvre et grippe, repos prescrit par le médecin..."
                  rows={3}
                />
              </div>

              {/* Pièce jointe */}
              <div className="new-justif-section">
                <div className="section-title">
                  <i className="ti ti-paperclip" />
                  Pièce jointe (PDF, JPG, PNG)
                </div>
                <div className="file-picker" onClick={pickFile}>
                  {pieceJointe ? (
                    <div className="file-selected">
                      <div>
                        <i className="ti ti-check-circle" />
                        <p className="name">📎 {pieceJointeNom}</p>
                        <button
                          className="remove-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPieceJointe(null);
                            setPieceJointeNom(null);
                          }}
                        >
                          <i className="ti ti-trash" />
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <i className="ti ti-cloud-upload" />
                      <p>Appuyer pour joindre un fichier</p>
                      <p className="sub">PDF, JPG, PNG acceptés</p>
                    </div>
                  )}
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                style={{ display: "none" }}
                accept=".pdf,.jpg,.jpeg,.png"
              />

              {/* Bouton soumettre */}
              <button
                className="submit-btn"
                onClick={handleSubmit}
                disabled={!inscriptionId || submitting}
              >
                {submitting ? (
                  <>
                    <span className="btn-spinner" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <i className="ti ti-paperclip" />
                    Soumettre la justification
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}